import { useId, type FC, type ChangeEventHandler, type MouseEvent } from 'react';
import { useIntl } from 'dibs-react-intl';
import classnames from 'classnames';
import slugify from 'dibs-slugify/exports/slugify.ts';

// components
import { AbsoluteAnchor } from '../../../global/AbsoluteAnchor/AbsoluteAnchor.jsx';

import { useServerVarsContext } from '../../../global/ServerVarsContext/ServerVarsContext.tsx';
import { useSbSelector } from '../../../reducers/useSbSelector.ts';

// styles
import styles from './SbRespRefineMenuStoneCutSelectInput.scss';
import dibsCss from 'dibs-css';

type Props = {
    stoneCut: string;
    count?: number | null;
    checked: boolean;
    isHidden?: boolean;
    linkReference?: string | null;
    linkable?: boolean | null;
    text: string;
    stoneCutMap: Record<string, FC<{ className?: string }>>;
    onChange: ChangeEventHandler<HTMLInputElement>;
};

export const SbRespRefineMenuStoneCutSelectInput: FC<Props> = ({
    stoneCut,
    count,
    checked,
    isHidden,
    linkReference,
    linkable,
    text,
    stoneCutMap,
    onChange,
}) => {
    const htmlId = useId();
    const intl = useIntl();
    const { isQuickFiltersRedesignVariant, isRemoveCountsVariant } = useServerVarsContext();
    const refetchInFlight = useSbSelector(state => state.filters.refetchInFlight);

    const StoneCutIcon = stoneCutMap[stoneCut];

    const id = `${htmlId}-${slugify(text)}`;

    return (
        <div
            className={classnames(
                isHidden ? dibsCss.hidden : dibsCss.flex,
                dibsCss.itemsCenter,
                dibsCss.pbSmall
            )}
        >
            <input
                id={id}
                className={styles.checkbox}
                checked={checked}
                type="checkbox"
                onChange={onChange}
            />
            <label
                htmlFor={id}
                className={classnames(
                    dibsCss.cursorPointer,
                    dibsCss.sassyFontBodySmall,
                    dibsCss.wFull
                )}
            >
                <AbsoluteAnchor
                    linkReference={linkReference}
                    linkable={linkable}
                    className={classnames(
                        styles.input,
                        dibsCss.textNoir,
                        dibsCss.wFull,
                        dibsCss.minW0,
                        dibsCss.pointerEventsNone,
                        dibsCss.flex,
                        dibsCss.itemsCenter
                    )}
                    dataTn={`${slugify(text)}-link${checked ? '-checked' : ''}`}
                    onClick={(e: MouseEvent<HTMLAnchorElement>) => e.preventDefault()}
                    tabIndex={-1}
                    inert
                >
                    <div
                        className={classnames(
                            styles.stoneCutContainer,
                            dibsCss.mrSmall,
                            refetchInFlight && isQuickFiltersRedesignVariant
                                ? dibsCss.textIron
                                : dibsCss.textStorm
                        )}
                    >
                        {StoneCutIcon && (
                            <StoneCutIcon className={checked ? dibsCss.textNoir : undefined} />
                        )}
                    </div>
                    {text}
                </AbsoluteAnchor>
            </label>
            {count && !isRemoveCountsVariant && (
                <div
                    className={classnames(
                        dibsCss.textBodySmallNormal,
                        dibsCss.mlXsmall,
                        refetchInFlight && isQuickFiltersRedesignVariant
                            ? dibsCss.textIron
                            : dibsCss.textSatan
                    )}
                >
                    {intl.formatNumber(count)}
                </div>
            )}
        </div>
    );
};
